import { type FormEvent, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AuthLayout } from '../components/AuthLayout'
import { PictureOfferCard } from '../components/PictureOfferCard'
import { Button, Field, FormError, Label } from '../components/ui'
import { api, call, messageOf } from '../lib/api'
import { requireUser } from '../lib/auth'
import { meQuery } from '../lib/queries'

export const Route = createFileRoute('/start')({
  beforeLoad: ({ context, location }) => requireUser(context.queryClient, location.href),
  component: Start,
})

function Start() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const done = (slug: string) => {
    qc.invalidateQueries({ queryKey: meQuery.queryKey })
    navigate({ to: '/l/$slug', params: { slug } })
  }
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const create = useMutation({
    mutationFn: () => call(api.POST('/leagues', { body: { name, timezone } })),
    onSuccess: (l) => done(l.slug),
  })
  const join = useMutation({
    // People paste whole links; take whatever follows the last slash.
    mutationFn: () => call(api.POST('/leagues/join', { body: { invite_code: code.trim().split('/').pop() ?? '' } })),
    onSuccess: (l) => done(l.slug),
  })
  const on = (fn: () => void) => (e: FormEvent) => {
    e.preventDefault()
    fn()
  }

  return (
    <AuthLayout title="Find your league" lede="Start one and invite your friends, or join theirs.">
      {/* Someone with no league never reaches a league page, so the offer is made here too (sc-2381). */}
      <PictureOfferCard />
      <form onSubmit={on(() => create.mutate())} className="grid gap-3">
        <Label>Start a league</Label>
        <FormError>{create.isError && messageOf(create.error)}</FormError>
        <Field label="League name" required minLength={2} maxLength={60} placeholder="Pine St. Cat League" value={name} onChange={(e) => setName(e.target.value)} help={`Round deadlines will follow your time zone (${timezone.replace(/_/g, ' ')}).`} />
        <Button type="submit" variant="primary" block busy={create.isPending}>
          Start the league
        </Button>
      </form>
      <div className="h-px bg-line" />
      <form onSubmit={on(() => join.mutate())} className="grid gap-3">
        <Label>Join with an invite</Label>
        <FormError>{join.isError && messageOf(join.error)}</FormError>
        <Field label="Invite link or code" required autoCapitalize="none" spellCheck={false} value={code} onChange={(e) => setCode(e.target.value)} />
        <Button type="submit" variant="ink" block busy={join.isPending}>
          Join
        </Button>
      </form>
    </AuthLayout>
  )
}
